// Single source of truth for per-role navigation. Consumed by the Sidebar
// (drawer), the mobile BottomNav, and the Ctrl+K CommandPalette — the three
// stay in sync by construction. Icons are stored as component REFERENCES
// (rendered <Icon /> by each consumer at its own size), and every navigable
// item carries an EXPLICIT `path` (no more deriving the URL segment from the
// label downstream).
//
// Item shape: { text, Icon, path, group?, collapsible?, keywords? }
//   - Section labels have Icon: null and no path.
//   - `group: "mb"` marks Media Buying sub-items (collapsible in the Sidebar).
//   - `keywords` are extra palette search terms (e.g. Prefixes ↔ "projects").
import {
  GridViewOutlined,
  VillaOutlined,
  PersonOutlined,
  Groups2Outlined,
  SettingsOutlined,
  CalculateOutlined,
  PaidOutlined,
  AssessmentOutlined,
  PaymentsOutlined,
  RuleOutlined,
  HandshakeOutlined,
  ReceiptLongOutlined,
  AccountBalanceWalletOutlined,
  SyncAltOutlined,
  CampaignOutlined,
  BadgeOutlined,
  TrendingUpOutlined,
  MoveToInboxOutlined,
  HistoryOutlined,
  SchoolOutlined,
  RateReviewOutlined,
  ReportProblemOutlined,
  PhotoLibraryOutlined,
  SwapHorizOutlined,
} from "@mui/icons-material";
import {
  isAdmin,
  isAccountManager,
  isFinancialManager,
  canViewFinancialDashboard,
  isMediaBuyer,
  isMediaBuyingHead,
  isMediaBuyingOnly,
  isMediaBuyingTeamLeader,
  canLeaderViewTab,
  canViewSquads,
  canViewWeekendDoubles,
} from "utils/roles";
import { leaderTabForPath } from "lib/leaderPerms";

// Media Buying sub-sections — rendered as proper sidebar nav items (instead of
// in-page tabs) under /media-buying/:section. Manager-only sections (Manage,
// History, Bonus) are appended for Admin + Media Buying Head, not plain Media
// Buyers.
export const MB_SECTION_ITEMS = [
  { text: "Overview", Icon: CampaignOutlined, path: "media-buying/overview", group: "mb" },
  {
    text: "Clients", Icon: PersonOutlined, path: "media-buying/clients", group: "mb",
    // A new client often lands near the end of a shift: the buyer mocks up a
    // fake campaign for approval and parks the real ones as drafts. That
    // workflow gets its own sub-page rather than another top-level tab.
    children: [{ text: "New Client ?", path: "media-buying/new-client" }],
  },
  { text: "Media Buyers", Icon: Groups2Outlined, path: "media-buying/buyers", group: "mb" },
  { text: "Interns", Icon: SchoolOutlined, path: "media-buying/interns", group: "mb", keywords: ["mentor", "mentees", "supervision"] },
  { text: "Projects", Icon: VillaOutlined, path: "media-buying/projects", group: "mb" },
  { text: "Feedback", Icon: RateReviewOutlined, path: "media-buying/feedback", group: "mb", keywords: ["qualified", "interested", "client feedback"] },
  { text: "Scaling", Icon: TrendingUpOutlined, path: "media-buying/scaling", group: "mb" },
  // Portfolio-wide best / worst ads: pause a dying ad, open its campaign in
  // Ads Manager. Visible to plain buyers too (the server scopes them to self).
  { text: "Creatives", Icon: PhotoLibraryOutlined, path: "media-buying/creatives", group: "mb", keywords: ["ads", "top ads", "weak ads", "pause ad", "ads manager"] },
  { text: "Partners", Icon: HandshakeOutlined, path: "media-buying/partners", group: "mb" },
  { text: "Inbox", Icon: MoveToInboxOutlined, path: "media-buying/inbox", group: "mb" },
];
// The two teams of the buying department: who is on each and who leads it.
//
// This REPLACED the monthly A/B/C competition that used to live on this tab —
// three rotating teams chasing a prize. The org chart is what the tab is for
// now, so the path is reused rather than adding a second "Teams" nobody could
// tell apart. Admin + Media Buying Head, via canViewSquads.
export const MB_TEAMS_ITEM = { text: "Teams", Icon: Groups2Outlined, path: "media-buying/teams", group: "mb", keywords: ["team", "teams", "team leader", "squad", "hana", "who is on", "members"] };

// A Team Leader's own team — the same card, narrowed to theirs by the server.
export const MB_MY_TEAM_ITEM = { text: "My Team", Icon: Groups2Outlined, path: "media-buying/my-team", group: "mb", keywords: ["my team", "team", "members", "my buyers"] };

// Who covers whom on Friday / Saturday. Same three people as Teams (the cover
// itself applies to every buyer; only the page is private).
export const MB_WEEKEND_DOUBLES_ITEM = { text: "Weekend Doubles", Icon: SwapHorizOutlined, path: "media-buying/weekend-doubles", group: "mb", keywords: ["cover", "friday", "saturday", "double", "weekend"] };

// The MB section items this user should see: the shared list, with the
// named-people pages slotted in after Media Buyers for those people.
export const mbSectionItemsFor = (user) => {
  const extra = [
    ...(canViewSquads(user) ? [MB_TEAMS_ITEM] : []),
    ...(canViewWeekendDoubles(user) ? [MB_WEEKEND_DOUBLES_ITEM] : []),
  ];
  if (!extra.length) return MB_SECTION_ITEMS;
  const i = MB_SECTION_ITEMS.findIndex((it) => it.path === "media-buying/buyers");
  return [...MB_SECTION_ITEMS.slice(0, i + 1), ...extra, ...MB_SECTION_ITEMS.slice(i + 1)];
};

// The Media Buying items a Team Leader sees: the shared section plus Manage,
// each one filtered by what an Admin has left open for them.
//
// What is deliberately absent, and why:
//   Bonus, Profit per Buyer — finance. Sealed, not toggleable.
//   History, System Issues, Inbox — module-wide by nature, so they cannot be
//     narrowed to one squad honestly (see LEADER_FORBIDDEN_TABS on the server).
//   Client Management, Leads, Financials — the CRM proper. A leader is sealed
//     to the module, like a plain buyer.
export const mbLeaderItemsFor = (user) => {
  const allowed = (item) => {
    const tab = leaderTabForPath(item.path);
    // An item with no entry in the catalog is not a leader surface at all.
    return tab ? canLeaderViewTab(user, tab.key) : false;
  };
  // My Team sits right after Overview: it is the leader's home view of their
  // own people, and the thing they open the module for.
  return [
    ...MB_SECTION_ITEMS.filter(allowed).flatMap((it) =>
      it.path === "media-buying/overview" && allowed(MB_MY_TEAM_ITEM)
        ? [it, MB_MY_TEAM_ITEM]
        : [it],
    ),
    ...MB_MANAGER_ITEMS.filter(allowed),
  ];
};

export const MB_MANAGER_ITEMS = [
  { text: "Manage", Icon: SettingsOutlined, path: "media-buying/manage", group: "mb" },
  { text: "History", Icon: HistoryOutlined, path: "media-buying/history", group: "mb" },
  { text: "Bonus", Icon: CalculateOutlined, path: "media-buying/bonus", group: "mb" },
  { text: "System Issues", Icon: ReportProblemOutlined, path: "media-buying/system-issues", group: "mb", keywords: ["automation", "faults", "hold failed", "errors"] },
];

// ADMIN-ONLY Media Buying items. MB_MANAGER_ITEMS above is Admin + Media Buying
// Head; this one is Admin alone — it's the module's only view of what clients
// are charged and what the company makes on each buyer.
export const MB_ADMIN_ITEMS = [
  {
    text: "Profit per Buyer",
    Icon: PaidOutlined,
    path: "media-buying/profit",
    group: "mb",
    keywords: ["margin", "profit", "earnings", "money", "charged", "made"],
  },
];

// Leads assigned to partner accounts (Client.isPartner — Phieg, Byoutak,
// Wiser, Whispyr, …) live on their own tab; the main Leads tab keeps the
// regular clients plus everything unassigned. Same grid, one extra query key
// (`partnerScope`). Admin + Media Buying Head — the two roles whose lead desk
// is not clamped to a managed set of clients.
export const PARTNER_LEADS_ITEM = {
  text: "Partner Leads",
  Icon: HandshakeOutlined,
  path: "partner-leads",
  keywords: ["partners", "phieg", "byoutak", "wiser", "whispyr", "caitlyn"],
};

const FINANCIAL_ITEMS = [
  { text: "Financial Dashboard", Icon: AssessmentOutlined, path: "financialdashboard", keywords: ["finance"] },
  { text: "Budget Tracker", Icon: AccountBalanceWalletOutlined, path: "budgettracker", keywords: ["balance", "bt"] },
  { text: "Mappings", Icon: RuleOutlined, path: "mappings" },
  { text: "Partners", Icon: HandshakeOutlined, path: "partners" },
  { text: "Invoices", Icon: ReceiptLongOutlined, path: "invoices" },
  { text: "Client Settings", Icon: PaymentsOutlined, path: "clientsettings", keywords: ["pricing"] },
];

// Per-role sidebar structure — exactly the arrays Sidebar.js used to build
// inline, with explicit paths.
export const getSidebarItems = (user) => {
  if (!user) return [];

  if (isFinancialManager(user)) {
    // Dedicated Financial Manager nav — sees ONLY financial pages. "Clients"
    // is the FM-scoped roster (names + activate/deactivate only), not the
    // admin Clients page.
    return [
      { text: "Financial Dashboard", Icon: AssessmentOutlined, path: "financialdashboard" },
      { text: "Budget Tracker", Icon: AccountBalanceWalletOutlined, path: "budgettracker" },
      { text: "Clients", Icon: PersonOutlined, path: "clients" },
      { text: "Operations", Icon: null },
      { text: "Mappings", Icon: RuleOutlined, path: "mappings" },
      { text: "Partners", Icon: HandshakeOutlined, path: "partners" },
      { text: "Invoices", Icon: ReceiptLongOutlined, path: "invoices" },
      { text: "Client Settings", Icon: PaymentsOutlined, path: "clientsettings" },
    ];
  }

  if (isMediaBuyer(user)) {
    // Media Buyer — sealed to the Media Buying module only.
    return [
      { text: "Media Buying", Icon: null, collapsible: "mb" },
      ...mbSectionItemsFor(user),
    ];
  }

  if (isMediaBuyingHead(user)) {
    // Media Buying Head — full Media Buying oversight, the Budget Tracker as
    // their single financial-related surface, and the CRM lead desk: the leads
    // their campaigns produced, plus the clients to assign them to.
    // Ordered like an Admin's sidebar — CRM first, Financials, Media Buying
    // last — using only the surfaces this role can actually reach. Matching the
    // Admin shape means anyone moving between the two accounts finds the same
    // things in the same places. "Profit per Buyer" stays out: it is
    // MB_ADMIN_ITEMS, Admin alone.
    return [
      { text: "Client Management", Icon: null },
      { text: "Clients", Icon: PersonOutlined, path: "clients" },
      { text: "Leads", Icon: Groups2Outlined, path: "leads" },
      PARTNER_LEADS_ITEM,
      { text: "Financials", Icon: null },
      { text: "Financials", Icon: AssessmentOutlined, path: "headfinancials" },
      { text: "Budget Tracker", Icon: AccountBalanceWalletOutlined, path: "budgettracker" },
      { text: "Media Buying", Icon: null, collapsible: "mb" },
      ...mbSectionItemsFor(user),
      ...MB_MANAGER_ITEMS,
    ];
  }

  if (isMediaBuyingTeamLeader(user)) {
    // Media Buying Team Leader — the module, clamped to their own squad by the
    // server, with finance and the CRM sealed off. One section, because that
    // is the whole of their job here.
    return [
      { text: "Media Buying", Icon: null, collapsible: "mb" },
      ...mbLeaderItemsFor(user),
    ];
  }

  if (isAdmin(user)) {
    const adminNav = [
      { text: "Dashboard", Icon: GridViewOutlined, path: "dashboard", keywords: ["home"] },
      { text: "Client Management", Icon: null },
      // "Prefixes" tab removed: prefixes are created with the client (auto
      // from the name / Advanced override on the create form) and edited via
      // Update Client, so a standalone tab no longer earns its place. The
      // /prefixes/* routes still exist for deep links.
      { text: "Clients", Icon: PersonOutlined, path: "clients" },
      { text: "Leads", Icon: Groups2Outlined, path: "leads" },
      PARTNER_LEADS_ITEM,
      { text: "Broadcast", Icon: CampaignOutlined, path: "broadcast", keywords: ["email", "announcement", "market update", "newsletter"] },
      { text: "Admin", Icon: null },
      { text: "Team", Icon: BadgeOutlined, path: "team", keywords: ["staff", "interns", "full timers", "media buyers", "mentor"] },
      { text: "CRM Integrations", Icon: SyncAltOutlined, path: "crm-delivery", keywords: ["delivery", "forwarding"] },
      { text: "Settings", Icon: SettingsOutlined, path: "settings" },
    ];
    // Every Admin gets a Financials section appended to their normal CRM nav.
    if (canViewFinancialDashboard(user)) {
      adminNav.push({ text: "Financials", Icon: null }, ...FINANCIAL_ITEMS);
    }
    // Media Buying module — appended to every Admin's nav (like Financials).
    adminNav.push(
      { text: "Media Buying", Icon: null, collapsible: "mb" },
      ...mbSectionItemsFor(user),
      ...MB_MANAGER_ITEMS,
      // Admin only — deliberately NOT added to the Media Buying Head branch.
      ...MB_ADMIN_ITEMS
    );
    return adminNav;
  }

  if (isAccountManager(user)) {
    return [
      { text: "Dashboard", Icon: GridViewOutlined, path: "dashboard", keywords: ["home"] },
      { text: "Managed Clients", Icon: null },
      { text: "Clients", Icon: PersonOutlined, path: "clients" },
      { text: "Leads", Icon: Groups2Outlined, path: "leads" },
      { text: "Broadcast", Icon: CampaignOutlined, path: "broadcast", keywords: ["email", "announcement", "market update", "newsletter"] },
      { text: "Projects", Icon: VillaOutlined, path: "am-projects", keywords: ["recommendations", "fake campaign", "showcase", "top performing"] },
      { text: "My Prospects", Icon: null },
      { text: "My Leads", Icon: Groups2Outlined, path: "myleads", keywords: ["prospects", "personal"] },
      { text: "Account", Icon: null },
      { text: "Settings", Icon: SettingsOutlined, path: "settings" },
    ];
  }

  // Client / Team Member / Super Client navigate to unprefixed entry routes
  // (e.g. /leads, /settings) that each role's router redirects to its own
  // prefixed page (e.g. /clientleads) — same contract as before.
  if (user.role === "Client") {
    return [
      { text: "Dashboard", Icon: GridViewOutlined, path: "dashboard", keywords: ["home"] },
      { text: "Team Management", Icon: null },
      { text: "Leads", Icon: Groups2Outlined, path: "leads" },
      { text: "Team Members", Icon: PersonOutlined, path: "teammembers", keywords: ["team"] },
      { text: "Admin", Icon: null },
      { text: "Settings", Icon: SettingsOutlined, path: "settings" },
    ];
  }

  if (user.role === "Team Member") {
    return [
      { text: "Dashboard", Icon: GridViewOutlined, path: "dashboard", keywords: ["home"] },
      { text: "Leads", Icon: Groups2Outlined, path: "leads" },
    ];
  }

  if (user.role === "Super Client") {
    return [
      { text: "Dashboard", Icon: GridViewOutlined, path: "dashboard", keywords: ["home"] },
      { text: "View", Icon: null },
      { text: "Leads", Icon: Groups2Outlined, path: "leads" },
      { text: "Account", Icon: null },
      { text: "Settings", Icon: SettingsOutlined, path: "settings" },
    ];
  }

  return [];
};

// Mobile bottom-bar variants — exactly what BottomNav.jsx used to declare
// inline.
export const getBottomNavItems = (user) => {
  if (isFinancialManager(user)) {
    // FM was the one role with no bottom bar (hamburger-only on phones).
    // Top five financial surfaces; Mappings / Partners / Activity stay in
    // the drawer.
    return [
      { value: "dashboard", label: "Home", Icon: AssessmentOutlined, path: "/financialdashboard" },
      { value: "budget", label: "Budget", Icon: AccountBalanceWalletOutlined, path: "/budgettracker" },
      { value: "invoices", label: "Invoices", Icon: ReceiptLongOutlined, path: "/invoices" },
      { value: "clients", label: "Clients", Icon: PersonOutlined, path: "/clients" },
      { value: "pricing", label: "Pricing", Icon: PaymentsOutlined, path: "/clientsettings" },
    ];
  }
  if (isMediaBuyingOnly(user)) {
    return [
      { value: "mediabuying", label: "Media Buying", Icon: CampaignOutlined, path: "/media-buying/overview" },
      { value: "inbox", label: "Inbox", Icon: MoveToInboxOutlined, path: "/media-buying/inbox", badge: true },
      { value: "settings", label: "Settings", Icon: SettingsOutlined, path: "/settings" },
    ];
  }
  if (isAdmin(user)) {
    // Five is the practical max on a phone — labels kept short.
    return [
      { value: "dashboard", label: "Home", Icon: GridViewOutlined, path: "/dashboard" },
      { value: "leads", label: "Leads", Icon: Groups2Outlined, path: "/leads" },
      { value: "clients", label: "Clients", Icon: PersonOutlined, path: "/clients" },
      { value: "mediabuying", label: "Media", Icon: CampaignOutlined, path: "/media-buying/overview" },
      { value: "settings", label: "Settings", Icon: SettingsOutlined, path: "/settings" },
    ];
  }
  return [
    { value: "dashboard", label: "Home", Icon: GridViewOutlined, path: "/dashboard" },
    { value: "leads", label: "Leads", Icon: Groups2Outlined, path: "/leads" },
    { value: "settings", label: "Settings", Icon: SettingsOutlined, path: "/settings" },
  ];
};

// Flattened page actions for the CommandPalette: navigable sidebar items with
// their section label carried as `group`, ready for client-side filtering.
export const getPaletteActions = (user) => {
  const actions = [];
  let currentGroup = "";
  for (const item of getSidebarItems(user)) {
    if (!item.path) {
      currentGroup = item.text;
      continue;
    }
    actions.push({
      id: item.path,
      label: item.text,
      path: `/${item.path}`,
      Icon: item.Icon,
      group: currentGroup,
      keywords: item.keywords || [],
    });
  }
  return actions;
};
